import { html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Historian } from '@vitesse/proto/vitesse/v1/historian_pb.js';
import { ModelSectionBase } from './model-section-base';
import { boundControllerIds, classifyBindingTarget, rebindControllers, rebindSpec } from './binding-target';
import { connectedInputs, connectionsOf, type ModelView } from '../../../core/domain/model';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { chip } from '@vitesse/base-ui/components/templates';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import '@vitesse/base-ui/elements/vitesse-icon';
import { SLOT, formatList, formatNumber, getLocale, listOf, t, withSlot } from '@vitesse/base-ui/i18n';
import '@vitesse/base-ui/elements/vitesse-gated';
import type { ControllerManager } from '../../managers/controller-manager';
import type { BackendClient } from '../../bridge/backend-client';
import type { AiApi } from '@vitesse/base-ui/ai';
import type { HistorySource } from '../../../sandbox/statechart/replay';
import type { SimulationResult } from '../../../sandbox/statechart/simulate';
import type { CatalogHit } from '../../../sandbox/statechart/sidecar-facts';
import '../../shell/vitesse-code-view';
import type { ReplayResult, SkippedInput } from '../../../sandbox/statechart/replay';
import type { ScanEpisode, ScanResult } from '../../../sandbox/statechart/scan';
import { isEmptyFilter } from '../../../sandbox/statechart/scan-trace';
// A thin adapter over the worker, small enough for the entry chunk.
import { historyFromBackend } from '../../../sandbox/statechart/history-source';
import type { CheckOptions, GroupSpec, LanguageDiagnostic } from '@vitesse/statechart-host';
import { checkDocument, MAX_FINDINGS } from '../../ai-tools/model-check';
import { draftOf, optionsOf, runsAsGroup, specOf } from '../../ai-tools/model-draft';
import { portTypes } from '../../ai-tools/port-types';
import {
  analyzeReplayCoverage,
  parseCoverageTree,
  plcWritePaths,
  type Blocked,
  type EdgeCoverage,
  type ReplayCoverage,
  type StateCoverage,
} from './replay-coverage';
import { EMPTY_SELECTION, pickerMode, toggleSelectedState } from './activity-filter';
import { ActivitySearch } from './activity-search';
import { skippedReason } from './skipped-reason';
import { parseAssumed } from './sidecar-assumed';
import { numberExtent } from '../../../sandbox/statechart/extent';
import { formatLength, formatMoment, formatSpan, formatWhen } from './statechart-replay-model';

/*
 * <vitesse-model-simulate> — THE MANUAL DOOR to the replay sandbox.
 *
 * A person picks a window of the past and presses Run; this replays the model's
 * own SCXML against what the historian recorded and hands the trace to the
 * viewer. NOTHING IS DEPLOYED AND NOTHING IS WRITTEN — the engine runs in the
 * opaque-origin sandbox frame, and every write the logic would have made is
 * captured as a trace entry that goes nowhere. That is a property of the
 * sandbox, not a promise made here.
 *
 * AGAINST WHICH DEVICE. A model's wiring already names the controllers it is
 * connected to, so those are the default. The picker exists for the other
 * question — "how would this logic have behaved on the line next door?" — and
 * rewrites only the controller half of a connected path, leaving the symbol
 * alone. A swapped device is read from and written to as one.
 *
 * WHAT RUNS (Part 7 of docs/hierarchical-models-step-3-plan.md). A model with
 * children, or with wiring of its own, runs as a group on the edge (a group of
 * one when it has no children), so it is replayed, searched and simulated as
 * one, as `models.simulate` does. A model with neither reads nothing from
 * outside, and the driver says so.
 *
 * REFUSALS ARE THE PRODUCT, not an error state. A model that can never receive
 * an event, a controller the historian never recorded, a window past the end of
 * a device's history: each comes back from the driver as a sentence, and this
 * shows the sentence. An empty chart in those cases would read as "it did
 * nothing", which is the one thing that must never happen here.
 *
 * LOADED ON DEMAND. The driver, the sandbox session and the viewer are pulled in
 * by `await import()` inside run(), not at module scope: the model page is a
 * route in the entry chunk, and most visits to it never replay anything.
 *
 * AND THE SIMULATION (docs/simulate-divergence-timeline-plan.md, step 9). Simulate
 * does the whole chain once: the Replay if it has not run, the facts measured
 * from the recording, the AI asked for a sidecar through the worker's route,
 * the compile gate through Monaco's worker, and the run in the sandbox, which
 * is the load gate — the sidecar's code is evaluated only there, behind the
 * frame's CSP. The viewer is then switched to the simulation, and the Sidecar
 * panel under it shows what the AI wrote and what it assumed. Nothing runs
 * while the playhead moves: the run is a finished record the viewer reads.
 *
 * A SIDECAR IS KEPT IN THIS BROWSER, per model, under a hash of the SCXML and
 * the wiring (decision 7): pressing Simulate again reuses it without a call,
 * until the model changes or Regenerate is pressed. A server refusal — credit,
 * `ai:use`, `ai:sandbox:run`, the org's code execution — is shown in the
 * server's words with no simulated chart; the Replay above it stays.
 */

/*
 * How far back "Find activity" looks — the person's choice, three days by
 * default. The span is most of what a search costs, and a week of a busy
 * model is a long wait. It is offered anyway because the search filters as it
 * goes: the episode cap counts only the periods that were asked for, so a wide
 * span does not drop the ones a person came for. The option labels say the
 * span, so the button's own label does not.
 */
const FIND_SPANS: readonly {
  id: string;
  labelKey: 'model.replayFindSpan1d' | 'model.replayFindSpan3d' | 'model.replayFindSpan7d';
  spanMs: number;
}[] = [
  { id: '1d', labelKey: 'model.replayFindSpan1d', spanMs: 86_400_000 },
  { id: '3d', labelKey: 'model.replayFindSpan3d', spanMs: 3 * 86_400_000 },
  { id: '7d', labelKey: 'model.replayFindSpan7d', spanMs: 7 * 86_400_000 },
];

/** The window presets, the History tab's own three. */
const PRESETS: readonly {
  id: string;
  labelKey: 'model.replayQuickLast15m' | 'model.replayQuickLastHour' | 'model.replayQuickLastDay';
  spanMs: number;
}[] = [
  { id: '15m', labelKey: 'model.replayQuickLast15m', spanMs: 15 * 60_000 },
  { id: '1h', labelKey: 'model.replayQuickLastHour', spanMs: 3_600_000 },
  { id: '1d', labelKey: 'model.replayQuickLastDay', spanMs: 86_400_000 },
];
const LAST_HOUR_MS = 3_600_000;
/** "include" around a picked search result: the period alone, or this much recording either side. */
const INCLUDE_OPTIONS_MS = [0, 60_000, 5 * 60_000, 15 * 60_000];
const INCLUDE_DEFAULT_MS = 5 * 60_000;
/** Search results listed before "Show N more". */
const RESULTS_SHOWN = 6;
let radioGroups = 0;
/** The quick-replay select's value for a window picked from the activity list. */
const CUSTOM = 'custom';
/** Bindings listed per device under "Read history from" before the rest fold
 *  behind a "show more" toggle. */
const MAX_SHOWN_BINDINGS = 2;
/** Where a model's sidecar is kept in this browser: `vitesse-sidecar:<model id>`. */
const SIDECAR_CACHE = 'vitesse-sidecar:';
/** How far the shadow may drift, as a share of the recorded range, before the panel says "with care". */
const SHADOW_CARE_SHARE = 0.1;

/** A sidecar the AI wrote and the gates accepted, as the panel and the cache hold it. */
interface KeptSidecar {
  code: string;
  js: string;
  summary: string;
  assumed: string[];
  /** The model that wrote it. */
  model: string;
}
type CachedSidecar = KeptSidecar & { hash: string };

type Stage = '' | 'facts' | 'asking' | 'compiling' | 'running';

@customElement('vitesse-model-simulate')
export class ModelSimulateSection extends ModelSectionBase {
  /** The worker handle — the replay's history source. */
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  /** The model's SCXML. The page has already fetched it for the statechart. */
  @property() scxml = '';
  /** This gateway's controllers — the "against" picker's options. */
  @property({ attribute: false }) controllers?: ControllerManager;
  /** The AI plane, for the sidecar route. Absent: Simulate says the plane is not connected. */
  @property({ attribute: false }) api?: AiApi;
  /** A test seam: where recorded history comes from instead of the backend. */
  @property({ attribute: false }) history?: HistorySource;

  @state() private spanMs = PRESETS[1].spanMs;
  @state() private busy = false;
  @state() private result: ReplayResult | null = null;
  /** The driver's own sentence when it declined to replay. */
  @state() private refusal = '';
  /** What the check found when it is the reason for the refusal: the same list models.simulate gives the assistant. */
  @state() private checkFindings: LanguageDiagnostic[] = [];
  /** Something broke that is not a refusal — a dead worker, a frame that never came up. */
  @state() private failure = '';
  @state() private progress: { done: number; total: number } | null = null;
  /** bound controller id → the controller to read history from instead. */
  @state() private against: Record<string, string> = {};
  /** Connected controller ids whose full list of inputs is unfolded under "Read history from". */
  @state() private unfolded: ReadonlySet<string> = new Set();
  /** "Read history from" open as a table, or folded to one line of chips. Only its header folds it. */
  @state() private sourceOpen = true;
  /** Once Find has results, where the replay's window comes from: a picked result, or a time range. */
  @state() private replaySource: 'result' | 'range' = 'result';
  /** How much recording either side of a picked result the replay includes. */
  @state() private includeMs = INCLUDE_DEFAULT_MS;
  /** The results table lists every row, not only the newest few. */
  @state() private allResults = false;
  /** The search result the table was last set up for, so its newest row is picked once, on arrival. */
  private seenFound: ScanResult | null = null;
  private readonly radioGroup = `ms-result-${++radioGroups}`;
  /** A window picked from the activity list, replacing the preset until one is clicked. */
  @state() private customWindow: { t0Ms: number; t1Ms: number } | null = null;

  // ── the simulation ──
  @state() private simulation: SimulationResult | null = null;
  @state() private sidecar: (KeptSidecar & { reused: boolean }) | null = null;
  /** Where the chain is, for the line beside the button. */
  @state() private stage: Stage = '';
  /** Seconds since Simulate was pressed, beside the stage — a wait that changes is not a hang. */
  @state() private elapsedS = 0;
  /** Which press of Simulate is live. Cancel bumps it; a run whose number is stale finishes into nothing. */
  private attempt = 0;
  /** The id of the sidecar request in flight, so abandoning the attempt can stop it (see `supersede`). */
  private aiRequest: string | null = null;
  private clock: ReturnType<typeof setInterval> | null = null;
  /** The server's or a gate's sentence when no sidecar could be had. */
  @state() private sidecarRefusal = '';
  /** What the route said to do about that refusal, when it said anything ('top up to continue'). */
  @state() private sidecarRemedy = '';
  /** The code the gates refused, shown beside the refusal so the person can see what the AI wrote. */
  @state() private sidecarShown: string | null = null;
  @state() private sidecarFailure = '';
  /** Why the simulation is the recording — said ABOVE the chart, with the viewer left on the replay. */
  @state() private noDivergence = '';
  /** What the shown simulation was produced with — the sidecar's own staleness fingerprint. */
  private simulatedWith = '';

  /** "Find activity": its state and its search, rendered here. */
  private readonly search = new ActivitySearch(
    this,
    async () => {
      const [{ scan }, { StatechartSandbox }] = await Promise.all([
        import('../../../sandbox/statechart/scan'),
        import('../../../sandbox/statechart/session'),
      ]);
      return { scan, newSandbox: () => new StatechartSandbox() };
    },
    FIND_SPANS[1].spanMs,
  );

  /** Disposed on teardown so a replay cannot outlive the panel. */
  private session: { dispose(): void } | null = null;
  /** What the shown result was produced with, so a changed control can say the
   *  picture is stale instead of silently describing a different window. */
  private ranWith = '';
  /** The coverage check and what it was computed from — recomputed only when
   *  the document or the wiring changes, not on every render. */
  private coverage: { key: string; value: ReplayCoverage | null } | null = null;

  protected sectionHeading(): string {
    return t('model.simulate');
  }

  /* The controller list arrives asynchronously and can arrive AFTER this panel
   * first renders. Without this the picker draws from an empty or partial list
   * and never corrects itself — and a picker that quietly points at the wrong
   * device is how a replay reads the wrong machine's history. */
  private readonly onControllers = (): void => this.requestUpdate();

  /** Subscribe defensively: this panel only ever READS the controller list, so a
   *  source that cannot be subscribed to (a stand-in, a partly-built manager) is
   *  a reason to show a static list, never a reason to throw inside a tile. */
  private listenControllers(src: ControllerManager | undefined, on: boolean): void {
    const fn = on ? src?.addEventListener : src?.removeEventListener;
    if (typeof fn === 'function') fn.call(src, 'change', this.onControllers);
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.listenControllers(this.controllers, true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.listenControllers(this.controllers, false);
    // Leaving the page is the same as Cancel, without the sentence: nobody is left to read it.
    this.supersede();
  }

  private startClock(): void {
    this.stopClock();
    const started = Date.now();
    this.elapsedS = 0;
    this.clock = setInterval(() => {
      this.elapsedS = Math.floor((Date.now() - started) / 1000);
    }, 1000);
  }

  private stopClock(): void {
    if (this.clock !== null) clearInterval(this.clock);
    this.clock = null;
  }

  /*
   * ABANDON WHATEVER IS RUNNING — the one way this panel stops work, whether the person pressed
   * Cancel, moved to another model, or left the page.
   *
   * The attempt number moves on, so every late step and answer of the old attempt is dropped
   * (`run`, `simulateAi`). The AI request in flight is cancelled in the AI worker, which aborts the
   * fetch; the route hears that as its own request signal and stops the model call it is paying
   * for. Before this, Cancel only stopped the wait, and the call went on generating and billing
   * for up to two full deadlines. The sandbox and the clock stop, and the controls are usable again.
   */
  private supersede(): void {
    this.attempt++;
    if (this.aiRequest) {
      void this.api?.cancelSidecar?.(this.aiRequest);
      this.aiRequest = null;
    }
    this.stopClock();
    this.session?.dispose();
    this.session = null;
    this.busy = false;
    this.stage = '';
    this.progress = null;
  }

  private cancelAi(): void {
    if (!this.stage) return;
    this.supersede();
    this.sidecarRefusal = t('model.simulateSidecarCancelled');
    this.sidecarRemedy = '';
  }

  protected willUpdate(changed: PropertyValues): void {
    // A different model or document: nothing on screen describes it any more.
    if (this.hasUpdated && (changed.has('modelId') || changed.has('scxml'))) this.resetForModel(changed.has('modelId'));
    // A different model, document or device makes an activity list describe something else. Two
    // models can have the same document, so a different model clears the list too.
    if (changed.has('scxml')) this.search.resetForDocument();
    else if (changed.has('modelId') || changed.has('against')) this.search.clear();
    // New results: back to the results tab, the table folded, and the newest row picked, so Run
    // under it always has something to replay.
    const found = this.search.found;
    if (found !== this.seenFound) {
      this.seenFound = found;
      this.replaySource = 'result';
      this.allResults = false;
      if (found?.episodes.length) void this.pickEpisode(found.episodes.length - 1);
    }
    if (changed.has('controllers')) {
      this.listenControllers(changed.get('controllers') as ControllerManager | undefined, false);
      this.listenControllers(this.controllers, true);
    }
  }

  /*
   * Force each picker to the controller it is actually replaying against.
   *
   * A <select> whose chosen value has no matching <option> shows its FIRST
   * option instead, with no error — so a bound controller missing from the list
   * (still loading, renamed, removed) renders as a confident, wrong answer. The
   * options always include the bound id now, and this closes the gap for good:
   * after every render the element agrees with the state or it is corrected.
   */
  protected updated(): void {
    for (const sel of this.querySelectorAll<HTMLSelectElement>('select[data-bound]')) {
      const want = this.against[sel.dataset.bound!] ?? sel.dataset.bound!;
      if (sel.value !== want) sel.value = want;
    }
  }

  /*
   * A DIFFERENT MODEL, OR A DIFFERENT DOCUMENT, STARTS FROM NOTHING.
   *
   * The model page keeps this panel when you move from one model to the next and
   * only changes its inputs. Everything below described the old one. Left in
   * place, the old replay was drawn under the new document's states; the
   * staleness check could not see it, since it compares the window and the
   * wiring, and two models often share both; and pressing Simulate built the
   * new model's sidecar from the old model's replay and cached it under the new
   * model's name.
   *
   * Whatever is still running for the old one is abandoned: the attempt number
   * moves on, so its late answers are dropped (see `run` and `simulateAi`), and
   * its sandbox goes now. The replay controls stay; which controllers to read
   * from was a choice about the old model's wiring, so it goes with the model.
   */
  private resetForModel(modelChanged: boolean): void {
    this.supersede();
    this.result = null;
    this.refusal = '';
    this.checkFindings = [];
    this.failure = '';
    this.ranWith = '';
    this.simulation = null;
    this.sidecar = null;
    this.sidecarRefusal = '';
    this.sidecarRemedy = '';
    this.sidecarShown = null;
    this.sidecarFailure = '';
    this.noDivergence = '';
    this.simulatedWith = '';
    this.customWindow = null;
    if (modelChanged) {
      this.against = {};
      this.unfolded = new Set();
    }
  }

  /** What the current controls would replay — the staleness fingerprint. */
  private signature(): string {
    const m = this.model;
    const win = this.customWindow ? `${this.customWindow.t0Ms}-${this.customWindow.t1Ms}` : this.spanMs;
    return `${win}|${JSON.stringify(m ? rebindControllers(connectionsOf(m), this.against) : {})}`;
  }

  /**
   * What the replay runs, and what its check reads: the group spec of a model that runs as a group,
   * read whole from the relay as models.simulate reads it, with the "against" picker's controllers;
   * nothing for a model with no wiring and no children, which runs alone. A string, when the model
   * cannot be read whole.
   */
  private async whatRuns(m: ModelView, scxml: string): Promise<{ group?: GroupSpec; check: CheckOptions } | string> {
    const check: CheckOptions = { inputs: m.inputs, outputs: m.outputs, commands: m.commands, types: portTypes(m.iface) };
    const wired = m.children.length > 0 || (m.wiring?.inputs.length ?? 0) > 0 || (m.wiring?.outputs.length ?? 0) > 0;
    if (!wired || !this.models) return { check };
    // The device to replay against, as it was when the person asked: the read below can be slow.
    const against = this.against;
    const draft = draftOf(await this.models.getSource(this.modelId), m);
    if (!draft)
      return `Refused: "${m.name}" is a group, and this relay is older than Part 5 of the Step 3 plan, so it does not send the group's templates to replay.`;
    if (!runsAsGroup(draft)) return { check: optionsOf(draft).root };
    const templates = Object.fromEntries(Object.entries(draft.templates).map(([name, tpl]) => [name, tpl.scxml]));
    return { group: rebindSpec(specOf(draft, { root: scxml, templates }), against), check: optionsOf(draft).root };
  }

  private async run(): Promise<void> {
    const m = this.model;
    if (!m || !this.backend || this.busy || !this.scxml) return;
    // A replay that finishes after the model or document changed describes neither.
    const mine = this.attempt;
    const live = (): boolean => mine === this.attempt;
    const scxml = this.scxml;
    this.busy = true;
    this.result = null;
    this.refusal = '';
    this.checkFindings = [];
    this.failure = '';
    this.progress = null;
    this.ranWith = this.signature();
    // A new replay is a new window: the simulation described the old one.
    this.simulation = null;
    this.sidecar = null;
    this.sidecarRefusal = '';
    this.sidecarRemedy = '';
    this.sidecarShown = null;
    this.sidecarFailure = '';
    this.noDivergence = '';
    const now = Date.now();
    const { t0Ms, t1Ms } = this.customWindow ?? { t0Ms: now - this.spanMs, t1Ms: now };
    const backend = this.backend;
    let sandbox: { dispose(): void } | null = null;
    try {
      /*
       * THE SAME CHECK THE ASSISTANT'S models.simulate RUNS, before anything boots: a document the
       * runner would refuse, or one with a name nothing declares, is not replayed, and the person
       * reads the same list, line by line, that the assistant would. Without it the replay refused
       * on the first syntax error alone, or failed on a compile error when the sandbox booted.
       */
      const runs = await this.whatRuns(m, scxml);
      if (!live()) return;
      if (typeof runs === 'string') {
        this.refusal = runs;
        return;
      }
      const checked = await checkDocument(scxml, runs.check);
      if (!live()) return;
      if (checked && checked.refusing.length > 0) {
        this.refusal = t('model.simulateCheckRefused', { count: checked.refusing.length });
        this.checkFindings = checked.refusing;
        return;
      }
      // The driver, the sandbox and the viewer arrive together, on demand —
      // inside the try, so a chunk that fails to load is a failure on screen
      // rather than a Run button stuck disabled.
      const [{ replay }, { StatechartSandbox }] = await Promise.all([
        import('../../../sandbox/statechart/replay'),
        import('../../../sandbox/statechart/session'),
        import('./vitesse-statechart-replay'),
      ]);
      if (!live()) return;
      const engine = new StatechartSandbox();
      sandbox = engine;
      this.session = engine;
      const out = await replay(
        {
          scxml,
          ...(runs.group ? { group: runs.group } : {}),
          outputs: m.outputs,
          t0Ms,
          t1Ms,
          onProgress: (done, total) => {
            if (live()) this.progress = { done, total };
          },
        },
        engine,
        this.historySource(backend),
      );
      if (!live()) return;
      if (out.ok) this.result = out;
      else this.refusal = out.refusal;
    } catch (e) {
      if (live()) this.failure = e instanceof Error ? e.message : String(e);
    } finally {
      sandbox?.dispose();
      if (sandbox && this.session === sandbox) this.session = null;
      if (live()) {
        this.busy = false;
        this.progress = null;
      }
    }
  }

  /** The check's findings under its refusal: each on its line, with its code, as the assistant reads them. */
  private renderCheckFindings(): TemplateResult | typeof nothing {
    if (this.checkFindings.length === 0) return nothing;
    const more = this.checkFindings.length - MAX_FINDINGS;
    return html`<ul class="ms-findings">
      ${this.checkFindings.slice(0, MAX_FINDINGS).map(
        (d) =>
          html`<li>
            <span class="ms-finding-line">${t('model.simulateFindingLine', { line: d.range.start.line + 1 })}</span>
            <code class="ms-finding-code">${d.code}</code>
            <span class="ms-finding-message">${d.message}</span>
          </li>`,
      )}
      ${more > 0 ? html`<li class="ms-finding-more">${t('model.simulateFindingsMore', { n: more })}</li>` : nothing}
    </ul>`;
  }

  private historySource(backend: BackendClient): HistorySource {
    return this.history ?? historyFromBackend(backend.api, this.gatewayId);
  }

  /** A command's neighbours are found by the controller's own name search, when the backend offers one. */
  private symbolSearch(): ((controllerId: string, parent: string) => Promise<CatalogHit[]>) | undefined {
    const api = this.backend?.api as
      { fetchSymbolPage?: (g: string, c: string, q: { fts: string; pageSize: number }) => Promise<{ rows: CatalogHit[] }> } | undefined;
    const search = api?.fetchSymbolPage;
    if (typeof search !== 'function') return undefined;
    const gw = this.gatewayId;
    return async (controllerId, parent) => {
      const page = await search.call(api, gw, controllerId, { fts: parent, pageSize: 50 });
      return page.rows.map((r) => ({ path: r.path, dataType: r.dataType, childCount: r.childCount }));
    };
  }

  /*
   * PRESSING SIMULATE — the whole chain, once.
   *
   * The Replay first, if the shown one is not for these controls; then the facts;
   * then, unless a kept sidecar fits, the AI through `obtainSidecar`, whose two
   * gates are Monaco's compiler and THE RUN ITSELF — a load refusal is the
   * sandbox's sentence, and a run that loads is the simulation kept. One retry,
   * then the code and the error are shown. Every wait has a line beside the button.
   */
  private async simulateAi(regenerate = false): Promise<void> {
    const m = this.model;
    if (!m || !this.backend || this.busy || !this.scxml) return;
    if (!this.api) {
      this.sidecarRefusal = t('model.simulateSidecarNoAi');
      this.sidecarRemedy = '';
      return;
    }
    if (!this.result || this.ranWith !== this.signature()) {
      await this.run();
      if (!this.result) return;
    }
    const replay = this.result;
    const api = this.api;
    const backend = this.backend;
    const scxml = this.scxml;
    const mine = ++this.attempt;
    const live = (): boolean => mine === this.attempt;
    // Every step that writes to the panel or spends a call checks first: a Cancel, or a different
    // model or document, moves the attempt on, and this chain must then stop, not finish quietly.
    // Thrown rather than returned, so the retry inside obtainSidecar stops too; the catch below
    // drops it because the attempt is no longer live.
    const stillLive = (): void => {
      if (!live()) throw new Error('superseded');
    };
    this.startClock();
    this.busy = true;
    this.simulation = null;
    this.sidecar = null;
    this.sidecarRefusal = '';
    this.sidecarRemedy = '';
    this.sidecarShown = null;
    this.sidecarFailure = '';
    this.noDivergence = '';
    this.simulatedWith = this.signature();
    // Held in an object: the load step below replaces it per attempt, from inside a closure.
    const held: { sandbox: { dispose(): void } | null } = { sandbox: null };
    try {
      // What the replay ran, read again: the sidecar is kept for the same model as it runs.
      const runs = await this.whatRuns(m, scxml);
      stillLive();
      if (typeof runs === 'string') {
        this.sidecarRefusal = runs;
        this.sidecarRemedy = '';
        return;
      }
      const group = runs.group;
      const [{ gatherSidecarFacts }, { obtainSidecar }, { monacoSidecarCompiler }, { simulate }, { StatechartSandbox }] = await Promise.all(
        [
          import('../../../sandbox/statechart/sidecar-facts'),
          import('../../../sandbox/statechart/sidecar-compile'),
          import('../../shell/sidecar-compiler'),
          import('../../../sandbox/statechart/simulate'),
          import('../../../sandbox/statechart/session'),
          import('./vitesse-statechart-replay'),
        ],
      );
      stillLive();
      const history = this.historySource(backend);
      this.stage = 'facts';
      const facts = await gatherSidecarFacts(replay, { history, search: this.symbolSearch() });
      if (!live()) return;
      // A model that never wrote has nothing a sidecar could respond to. Said now, before a call
      // is spent and twenty seconds pass writing physics for a plant nobody drives.
      if (facts.commands.length === 0) {
        this.noDivergence = t('model.simulateSidecarNoWrites');
        return;
      }
      // What the model wrote to each command, for the loader's probe to try.
      const commands = facts.commands.map((c) => ({
        path: c.path,
        written: [...new Set(facts.writes.filter((w) => w.path === c.path).map((w) => w.value))],
      }));
      const neighbourPaths = facts.commands.flatMap((c) => c.neighbours.map((n) => n.path));
      // Gate 2, and the run: the sandbox loads the sidecar and, if it loads, simulates.
      let kept: SimulationResult | null = null;
      let assumed: string[] | undefined;
      const load = async (js: string): Promise<{ ok: true } | { ok: false; refusal: string }> => {
        stillLive();
        this.stage = 'running';
        // A fresh sandbox for every attempt. A run the watchdog stopped leaves its sandbox dead,
        // and the retry that follows must not inherit it.
        held.sandbox?.dispose();
        const engine = new StatechartSandbox();
        held.sandbox = engine;
        this.session = engine;
        const out = await simulate(
          {
            scxml,
            ...(group ? { group } : {}),
            outputs: m.outputs,
            t0Ms: Number(replay.window.t0Ns / 1_000_000n),
            t1Ms: Number(replay.window.t1Ns / 1_000_000n),
            sidecar: { js },
            commands,
            neighbourPaths,
            assumed,
            replay: { finalState: replay.finalState, visited: replay.visited },
          },
          engine,
          history,
        );
        if (!out.ok) return { ok: false, refusal: out.refusal };
        kept = out;
        return { ok: true };
      };

      // A kept sidecar for this exact document and wiring costs no call.
      const hash = await fingerprint(scxml, group);
      stillLive();
      const cached = regenerate ? null : readCache(m.id, hash);
      if (cached) {
        assumed = cached.assumed;
        const loaded = await load(cached.js);
        if (!live()) return;
        if (loaded.ok && kept) {
          this.sidecar = { ...cached, reused: true };
          this.show(kept);
          return;
        }
        // It no longer loads — the loader grew a check, or the recording changed shape. Ask afresh.
        writeCache(m.id, null);
      }

      let wroteBy = '';
      const compile = await monacoSidecarCompiler();
      stillLive();
      let remedy = '';
      const out = await obtainSidecar(
        async (previous) => {
          stillLive();
          this.stage = 'asking';
          // Named, so `supersede` can stop it: a signal cannot cross to the AI worker.
          const requestId = crypto.randomUUID();
          this.aiRequest = requestId;
          const r = await api.requestSidecar({ facts, previous, label: m.name }, { requestId });
          if (this.aiRequest === requestId) this.aiRequest = null;
          stillLive();
          if (!r.ok) {
            remedy = r.remedy;
            return { ok: false, refusal: r.refusal };
          }
          wroteBy = r.model;
          assumed = r.assumed;
          return { ok: true, answer: { code: r.code, summary: r.summary, assumed: r.assumed } };
        },
        {
          compile: (source) => {
            stillLive();
            this.stage = 'compiling';
            return compile(source);
          },
          load,
        },
      );
      if (!live()) return;
      if (!out.ok) {
        this.sidecarRefusal = out.error;
        // Only the route's own refusals carry advice; a gate's refusal is shown with its code instead.
        this.sidecarRemedy = out.stage === 'ask' ? remedy : '';
        if (out.stage !== 'ask') this.sidecarShown = out.code;
        return;
      }
      const sidecar: KeptSidecar = {
        code: out.answer.code,
        js: out.js,
        summary: out.answer.summary,
        assumed: out.answer.assumed,
        model: wroteBy,
      };
      writeCache(m.id, { ...sidecar, hash });
      this.sidecar = { ...sidecar, reused: false };
      this.show(kept);
    } catch (e) {
      if (live()) this.sidecarFailure = e instanceof Error ? e.message : String(e);
    } finally {
      held.sandbox?.dispose();
      if (held.sandbox && this.session === held.sandbox) this.session = null;
      if (live()) {
        this.stopClock();
        this.busy = false;
        this.stage = '';
      }
    }
  }

  /** A run that diverged becomes the viewer's simulation; one that did not is said above the chart
   *  and the viewer stays on the replay, since the two pictures would be the same. */
  private show(kept: SimulationResult | null): void {
    if (kept && kept.simulated.length > 0) this.simulation = kept;
    else this.noDivergence = t('model.simulateSidecarNoDivergence');
  }

  protected renderContent() {
    const m = this.model;
    if (!m) return nothing;
    const bound = boundControllerIds(connectionsOf(m));
    const options = (this.controllers?.all ?? []).slice().sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
    return html`
      ${this.styles()}
      <p class="ms-heading"><strong>${t('model.simulateTitle')}</strong> <span class="ms-hint">${t('model.simulateHint')}</span></p>
      ${this.renderCoverage()} ${this.renderAgainst(bound, options)} ${this.renderQuery()} ${this.renderFind()} ${this.renderReplayCard()}
      ${this.renderBody()}
    `;
  }

  /*
   * A TIME RANGE — any stretch of history, no search needed. The preset picker
   * stays (the last 15 minutes, hour, …; a picked search result shows up in it
   * as its own option), with the stretch's length beside it. The bar under it
   * says exactly which stretch Run will replay, how long it is, and — once Find
   * has run — how many of the search's matches fall inside it.
   */
  private renderRange(f: ScanResult | null) {
    const locale = getLocale();
    const value = this.customWindow ? CUSTOM : String(this.spanMs);
    const windows = [
      ...(this.customWindow ? [{ value: CUSTOM, label: formatSpan(this.customWindow.t0Ms, this.customWindow.t1Ms, locale) }] : []),
      ...PRESETS.map((p) => ({ value: String(p.spanMs), label: t(p.labelKey) })),
    ];
    // A preset is "the last N": its exact ends are now, as Run will take them.
    const now = Date.now();
    const win = this.customWindow ?? { t0Ms: now - this.spanMs, t1Ms: now };
    const length = formatLength(win.t1Ms - win.t0Ms, locale);
    const parts = [length];
    if (f) {
      const inside = f.episodes.filter((ep) => Number(ep.endNs / 1_000_000n) >= win.t0Ms && Number(ep.startNs / 1_000_000n) <= win.t1Ms);
      parts.push(t('model.replayRangeMatches', { count: inside.length }));
    }
    return html`<div class="ms-range-row">
        <vitesse-select
          class="ms-window"
          size="md"
          .options=${windows}
          .value=${value}
          @change=${(e: CustomEvent<{ value: string }>) => {
            const v = e.detail.value;
            if (v === CUSTOM) return;
            this.spanMs = Number(v);
            this.customWindow = null;
            this.search.choose(-1);
          }}
        ></vitesse-select>
        <span class="ms-progress">${length}</span>
      </div>
      <div class="ms-pick ms-pick-range">
        <vitesse-icon class="ms-pick-icon" name="clock" size="16"></vitesse-icon>
        <div class="ms-pick-text">
          <span class="ms-pick-label">${t('model.replayRangeLabel')}</span>
          <span class="ms-pick-what"><strong>${formatSpan(win.t0Ms, win.t1Ms, locale)}</strong>${parts.map((x) => ` · ${x}`)}</span>
        </div>
        <span class="ms-pick-controls">${this.renderRunButtons(true)}</span>
      </div>
      <div class="ms-run-status">${this.renderRunStatus(nothing)}</div>`;
  }

  /** Run and Simulate — in the time-range row, and in the bar under a picked search result. */
  private renderRunButtons(simulateFilled: boolean) {
    return html`<vitesse-gated .rpc=${Historian.method.queryRange} inline
        >${button({
          label: this.busy ? t('model.simulateRunning') : t('model.simulateRun'),
          icon: 'play',
          color: 'neutral',
          onClick: () => void this.run(),
          disabled: this.busy || !this.scxml,
        })}</vitesse-gated
      >
      <vitesse-gated .rpc=${Historian.method.queryRange} inline
        >${button({
          label: t('model.simulateSidecarButton'),
          color: simulateFilled ? 'blue' : 'neutral',
          filled: simulateFilled,
          title: t('model.simulateSidecarHint'),
          onClick: () => void this.simulateAi(),
          disabled: this.busy || !this.scxml,
        })}</vitesse-gated
      >`;
  }

  /** What a run is doing, with Cancel while the AI writes; `idle` when nothing is. */
  private renderRunStatus(idle: TemplateResult | typeof nothing) {
    if (this.progress) return html`<span class="ms-progress">${this.progress.done} / ${this.progress.total}</span>`;
    if (this.stage)
      return html`<span class="ms-progress">${this.stageText()} · ${t('model.simulateSidecarElapsed', { seconds: this.elapsedS })}</span>
        ${button({ label: t('common.cancel'), color: 'neutral', onClick: () => this.cancelAi() })}`;
    return idle;
  }

  private stageText(): string {
    return {
      '': '',
      facts: t('model.simulateSidecarStageFacts'),
      asking: t('model.simulateSidecarStageAsking'),
      compiling: t('model.simulateSidecarStageCompiling'),
      running: t('model.simulateSidecarStageRunning'),
    }[this.stage];
  }

  /*
   * WHERE EACH BOUND DEVICE'S HISTORY IS READ FROM — shared by the search and
   * the replay.
   *
   * WHAT IT DOES: a connection is a whole path,
   * `controllers/Core/symbols/CPU.Usage`, and this rewrites ONLY the controller
   * in it. The symbol names never move. So it answers "how would this same
   * logic have behaved on the machine next door?", and never "compare these two
   * devices".
   *
   * A CARD THAT FOLDS. Open, it is a three-column table: the bound device, the
   * symbols read from it (`CPU.Usage → cpu`, as the Bindings panel sets them),
   * and the controller whose recordings stand in for it. Folded, it is one line:
   * the count, one chip per connected device naming the device it will read from, and
   * whether anything was swapped. Only the person folds it, from the header.
   * It once folded by itself when Find ran, and the whole panel jumped up under
   * the pointer just as the person pressed the button.
   *
   * ONE ROW PER DEVICE, NOT PER INPUT. The swap rewrites a controller, so every
   * connection to it moves together. A row per input would put two pickers on
   * the screen that are secretly one, and changing either would change both. A
   * device's symbols stack in the middle column instead, folded past
   * MAX_SHOWN_BINDINGS behind "Show N more".
   *
   * WHY DECODED. The path stores the symbol as a subject token
   * (`CPU_Core_x201_Usage`); the Bindings panel decodes it for display and so
   * must this, or the same connection reads differently two panels apart.
   *
   * WHEN THERE IS NOTHING TO SWAP — the gateway has no controller other than
   * the connected one — a select with one option is a question with one answer, so
   * the last column says the name as plain text. The card is still shown: it is
   * the inventory of this replay's data source.
   */
  private renderAgainst(bound: string[], options: { id: string; name?: string }[]) {
    if (bound.length === 0) return nothing;
    const connections = this.model ? connectionsOf(this.model) : {};
    const nameOf = (id: string) => options.find((c) => c.id === id)?.name || id;
    const readFrom = (id: string) => this.against[id] ?? id;
    // Which inputs read from this device, as `symbol → input`, in the wiring's order.
    const usedBy = (id: string) =>
      Object.entries(connections).flatMap(([name, path]) => {
        const c = classifyBindingTarget(path);
        return c.kind === 'controller' && c.controllerId === id ? [{ name, symbol: c.symbol }] : [];
      });
    const rows = bound.map((id) => ({ id, used: usedBy(id) }));
    const total = rows.reduce((n, r) => n + r.used.length, 0);
    const swapped = bound.filter((id) => readFrom(id) !== id).length;
    const status = swapped ? t('model.simulateAgainstSwapped', { count: swapped }) : t('model.simulateAgainstAllBound');
    const count = t('model.simulateAgainstCount', { count: total });
    const open = this.sourceOpen;
    const toggleOpen = () => (this.sourceOpen = !this.sourceOpen);

    const head = html`<button type="button" class="ms-src-head" aria-expanded=${open ? 'true' : 'false'} @click=${toggleOpen}>
      <vitesse-icon class="ms-src-chev" name=${open ? 'chevron-down' : 'chevron-right'} size="14"></vitesse-icon>
      <span class="ms-src-title">${t('model.simulateAgainstHeading')}</span>
      ${
        open
          ? html`<span class="ms-src-meta">${count} · ${status}</span>`
          : html`<span class="ms-src-meta">${count}</span>
              <span class="base-chips ms-src-chips">
                ${rows.flatMap((r) => r.used.map((u) => chip(`${nameOf(readFrom(r.id))} · ${u.name}`)))}
              </span>
              <span class="ms-src-meta ms-src-status">${status}</span>`
      }
    </button>`;
    if (!open) return html`<section class="ms-src">${head}</section>`;

    // A row has a choice when the gateway lists any controller besides the one the model is bound to.
    const hasChoice = bound.some((id) => options.some((c) => c.id !== id));
    const toggleMore = (id: string) => {
      const next = new Set(this.unfolded);
      if (!next.delete(id)) next.add(id);
      this.unfolded = next;
    };
    return html`<section class="ms-src">
      ${head}
      <div class="ms-src-table" role="table">
        <div class="ms-src-row ms-src-cols" role="row">
          <span role="columnheader">${t('model.simulateAgainstColBinding')}</span>
          <span role="columnheader">${t('model.simulateAgainstColSymbol')}</span>
          <span role="columnheader">${t('model.simulateAgainstColSource')}</span>
        </div>
        ${rows.map(({ id, used }) => {
          // The bound controller is ALWAYS offered, even when the manager has
          // not listed it — otherwise the select falls back to its first option
          // and shows a different device than the one being read.
          const opts = options.some((c) => c.id === id) ? options : [{ id, name: id }, ...options];
          const unfolded = this.unfolded.has(id);
          const shown = unfolded ? used : used.slice(0, MAX_SHOWN_BINDINGS);
          const hidden = used.length - MAX_SHOWN_BINDINGS;
          const asBound = (name: string) => t('model.simulateAgainstAsBound', { name });
          return html`<div class="ms-src-row" role="row">
            <span class="ms-src-device" role="cell">${nameOf(id)}</span>
            <span class="ms-src-symbols" role="cell">
              ${shown.map(
                (u) =>
                  html`<span class="ms-src-symbol">
                    <span class="ms-src-sym" title=${u.symbol}>${u.symbol}</span>
                    <span class="ms-src-arrow">→</span>
                    <span class="ms-src-input">${u.name}</span>
                  </span>`,
              )}
              ${
                hidden > 0
                  ? html`<button type="button" class="ms-src-more" aria-expanded=${unfolded} @click=${() => toggleMore(id)}>
                      ${unfolded ? t('model.simulateAgainstLess') : t('model.simulateAgainstMore', { count: hidden })}
                    </button>`
                  : nothing
              }
            </span>
            <span class="ms-src-pick" role="cell">
              ${
                hasChoice
                  ? html`<vitesse-select
                      size="md"
                      data-bound=${id}
                      .options=${opts.map((c) => ({ value: c.id, label: c.id === id ? asBound(c.name || c.id) : c.name || c.id }))}
                      .value=${readFrom(id)}
                      @change=${(e: CustomEvent<{ value: string }>) => (this.against = { ...this.against, [id]: e.detail.value })}
                    ></vitesse-select>`
                  : html`<span class="ms-src-fixed">${asBound(nameOf(id))}</span>`
              }
            </span>
          </div>`;
        })}
      </div>
    </section>`;
  }

  // ── find activity ────────────────────────────────────────────────────────

  private find(): void {
    const m = this.model;
    if (!m || !this.backend || !this.scxml) return;
    const scxml = this.scxml;
    const coverage = this.currentCoverage()?.states ?? null;
    const history = historyFromBackend(this.backend.api, this.gatewayId);
    // The search reads the group itself, after it is busy: a model switch, a new document or a
    // teardown during the read then ends it, and its refusal or failure replaces the last one.
    void this.search.find(async () => {
      const runs = await this.whatRuns(m, scxml);
      if (typeof runs === 'string') return runs;
      return {
        scxml,
        ...(runs.group ? { group: runs.group } : {}),
        coverage,
        allUnreachable: t('model.replayFindAllUnreachable'),
        history,
      };
    });
  }

  /** Picking a search result sets the window Run and Simulate replay: the period and `includeMs` either side. */
  private async pickEpisode(index: number): Promise<void> {
    const found = this.search.found;
    const ep = found?.episodes[index];
    if (!found || !ep) return;
    const { episodeWindow } = await import('../../../sandbox/statechart/scan');
    if (this.search.found !== found) return;
    this.customWindow = episodeWindow(ep, found.window, this.includeMs, 0);
    this.search.choose(index);
  }

  private hasResults(): boolean {
    return !this.search.refusal && !this.search.failure && (this.search.found?.episodes.length ?? 0) > 0;
  }

  /*
   * THE QUERY CARD — what to look for, the sentence it makes, and the button.
   * One surface, in the order it is used: pick chips, read "Your search" back,
   * press Find. The results sit under the card, and the Replay row under them,
   * so a period picked from the list is right above the Run that replays it.
   */
  private renderQuery() {
    const spans = FIND_SPANS.map((f) => ({ value: String(f.spanMs), label: t(f.labelKey) }));
    return html`<section class="ms-query">
      ${this.renderChips()}
      <div class="ms-search">
        <p class="ms-search-label"><vitesse-icon name="search" size="12"></vitesse-icon>${t('model.replayFindYourSearch')}</p>
        <div class="ms-search-body">
          <p class="ms-find-desc">${this.describeSelection()}</p>
          <div class="ms-find-row">
            <span class="ms-label">${t('model.replayFindSpanLabel')}</span>
            <vitesse-select
              class="ms-window"
              size="md"
              .options=${spans}
              .value=${String(this.search.spanMs)}
              @change=${(e: CustomEvent<{ value: string }>) => this.search.setSpan(Number(e.detail.value))}
            ></vitesse-select>
            <vitesse-gated .rpc=${Historian.method.queryRange} inline
              >${button({
                label: t('model.replayFindButton'),
                icon: 'search',
                color: 'blue',
                filled: true,
                onClick: () => this.find(),
                disabled: this.search.finding || !this.scxml,
              })}</vitesse-gated
            >
          </div>
        </div>
        ${
          this.search.finding
            ? html`<div class="ms-find-progress">
                <span class="ms-progress"
                  >${
                    this.search.expected === null
                      ? t('model.replayFindSearching', { percent: Math.floor(this.search.progress * 100) })
                      : t('model.replayFindSearchingRows', {
                          percent: Math.floor(this.search.progress * 100),
                          rows: formatNumber(this.search.expected),
                        })
                  }</span
                >
                ${button({ label: t('model.replayFindStop'), color: 'neutral', size: 'sm', onClick: () => this.search.stop() })}
              </div>`
            : nothing
        }
      </div>
    </section>`;
  }

  /** What Find came back with when there is nothing to list — a refusal, a failure, or "nothing happened".
   *  Periods it found are listed in the replay card instead. */
  private renderFind() {
    const f = this.search.found;
    if (this.search.refusal)
      return html`<div class="ms-find">
        <p class="ms-refusal">${this.search.refusal}</p>
      </div>`;
    if (this.search.failure)
      return html`<div class="ms-find">
        <p class="ms-failure">${t('model.simulateFailed', { reason: this.search.failure })}</p>
      </div>`;
    if (!f) return nothing;

    if (f.episodes.length > 0) return nothing; // listed in the replay card
    const { from, to, notes, total, timing } = this.findSummary(f);
    const heading =
      f.filter && total > 0
        ? t('model.replayFindNoneMatched', { count: total, from, to })
        : t('model.replayFindNone', { from, to, state: f.finalState.join('+') });
    return html`<div class="ms-find">
      <p class="ms-find-heading">${heading}</p>
      <p class="ms-find-timing">${timing}</p>
      ${notes.map((n) => html`<p class="ms-stale">${n}</p>`)}
      ${
        f.filter && f.hidden > 0
          ? // The question was narrow and found nothing; the wide answer is one click away.
            button({
              label: t('model.replayFindShowAll', { count: f.hidden }),
              icon: 'search',
              color: 'blue',
              size: 'sm',
              disabled: this.search.finding,
              onClick: () => {
                this.search.setSelection(EMPTY_SELECTION);
                this.find();
              },
            })
          : // "Why did it do nothing?" is answered by looking at the readings.
            button({
              label: t('model.replayFindReplayLastHour'),
              icon: 'play',
              color: 'blue',
              size: 'sm',
              disabled: this.busy,
              onClick: () => {
                this.spanMs = LAST_HOUR_MS;
                this.customWindow = null;
                this.search.choose(-1);
                void this.run();
              },
            })
      }
    </div>`;
  }

  /** What a finished search says about itself: its window, notes, match total and timing line. */
  private findSummary(f: ScanResult) {
    const locale = getLocale();
    const from = formatMoment(Number(f.window.t0Ns / 1_000_000n), locale, { date: true });
    const to = formatMoment(Number(f.window.t1Ns / 1_000_000n), locale, { date: true });
    const notes: string[] = [];
    if (f.cancelled) notes.push(t('model.replayFindStopped'));
    if (f.hung) notes.push(t('model.replayFindHung'));
    if (f.truncated.length)
      notes.push(
        t('model.replayStoryCutShort', { inputs: formatList(f.truncated.map((p) => f.inputs.find((i) => i.path === p)?.name ?? p)) }),
      );
    if (f.moreEpisodes) notes.push(t('model.replayFindMore', { count: f.episodes.length }));
    // The search went on without these; a list of episodes that never mentions
    // them reads as a search that covered every connected input.
    for (const s of f.skipped) notes.push(`${s.name ?? s.path} — ${skippedReason(s)}`);

    // With chips chosen, the count is of matches against everything found, so
    // "3 of 37" is never read as "the model acted three times". A filtered
    // search over a model that never acted at all gets the plain "nothing
    // happened" sentence: there is no "other periods" to speak of.
    const total = f.episodes.length + f.hidden;
    // Where the time went, in seconds: the two waits a slow search is made of.
    const sec = { maximumFractionDigits: 1 };
    const timing = t('model.replayFindTiming', {
      rows: formatNumber(f.rowCount),
      fetch: formatNumber(f.timing.fetchMs / 1000, sec),
      engine: formatNumber(f.timing.engineMs / 1000, sec),
      calls: formatNumber(f.timing.rangeCalls),
    });

    return { from, to, notes, total, timing };
  }

  /*
   * WHAT TO REPLAY. One card, always: a search result or a time range. Before
   * Find has listed anything the search-result side is off and the card is the
   * time range. With results, the newest is picked on arrival; picking only
   * sets the window, and Run or Simulate replays it, so a person can read the
   * row before spending the run.
   *
   * THE COLUMNS answer the questions a list of periods raises: when, for how
   * long, why it is here (what it matched — the chips from the query card),
   * how busy the model was (states visited) and whether it would have touched
   * the machine (writes; script errors beside them in red).
   */
  private renderReplayCard(): TemplateResult {
    const f = this.hasResults() ? this.search.found : null;
    const source = f ? this.replaySource : 'range';
    const sum = f ? this.findSummary(f) : null;
    const count = f?.episodes.length ?? 0;
    const meta =
      f && sum && source === 'result'
        ? f.filter
          ? t('model.replayFindMeta', { count, total: sum.total, from: sum.from, to: sum.to })
          : t('model.replayFindMetaAll', { count, from: sum.from, to: sum.to })
        : t('model.replayRangeMeta');
    const tab = (id: 'result' | 'range', icon: string, label: string, extra: unknown, off = false) =>
      html`<button
        type="button"
        role="tab"
        class=${`ms-seg-tab${source === id ? ' on' : ''}`}
        aria-selected=${source === id ? 'true' : 'false'}
        ?disabled=${off}
        title=${off ? t('model.replaySourceResultNone') : ''}
        @click=${() => (this.replaySource = id)}
      >
        <vitesse-icon name=${icon} size="14"></vitesse-icon>${label}${extra}
      </button>`;
    return html`<section class="ms-results">
      <header class="ms-results-head">
        <span class="ms-results-title">${t('model.replaySourceTitle')}</span>
        <div class="ms-seg" role="tablist">
          ${tab('result', 'search', t('model.replaySourceResult'), f ? html`<span class="ms-seg-count">${formatNumber(count)}</span>` : nothing, !f)}
          ${tab('range', 'clock', t('model.replaySourceRange'), nothing)}
        </div>
        <span class="ms-results-meta">${meta}</span>
      </header>
      ${source === 'range' || !f ? this.renderRange(f) : this.renderResultsBody(f)}
      ${
        sum
          ? html`${sum.notes.map((n) => html`<p class="ms-stale">${n}</p>`)}
              <p class="ms-find-timing">${sum.timing}</p>`
          : nothing
      }
    </section>`;
  }

  private renderResultsBody(f: ScanResult): TemplateResult {
    const matched = new Set(f.filter?.states ?? []);
    // Newest first: the recent past is what a person usually means.
    const rows = f.episodes.map((ep, i) => ({ ep, i })).reverse();
    const shown = this.allResults ? rows : rows.slice(0, RESULTS_SHOWN);
    const hidden = rows.length - shown.length;
    return html`<div class="ms-table">
        <div class="ms-table-scroll">
          <table>
            <thead>
              <tr>
                <th><span class="ms-sr">${t('model.replayColPick')}</span></th>
                <th>${t('model.replayColWhen')}</th>
                <th>${t('model.replayColLength')}</th>
                <th>${t('model.replayColMatched')}</th>
                <th>${t('model.replayColStates')}</th>
                <th>${t('model.replayColWrites')}</th>
              </tr>
            </thead>
            <tbody>
              ${shown.map(({ ep, i }) => this.renderResultRow(ep, i, f, matched))}
            </tbody>
          </table>
        </div>
        ${
          rows.length > RESULTS_SHOWN
            ? html`<button type="button" class="ms-results-more" @click=${() => (this.allResults = !this.allResults)}>
                ${this.allResults ? t('model.replayResultsFewer') : t('model.replayResultsMore', { count: hidden })}
              </button>`
            : nothing
        }
      </div>
      ${this.renderPicked(f, rows, matched)}`;
  }

  /** Why a result is in the list, as the query card's chips name it. */
  private matchedOn(ep: ScanEpisode, f: ScanResult, matched: Set<string>): string[] {
    if (!f.filter) return [t('model.replayMatchedAny')];
    const out = ep.entered.filter((st) => matched.has(st)).map((st) => t('model.replayMatchedEntered', { state: st }));
    if (f.filter.writes && ep.writes) out.push(t('model.replayMatchedWrites'));
    if (f.filter.errors && ep.errors) out.push(t('model.replayMatchedErrors'));
    return out;
  }

  private renderResultRow(ep: ScanEpisode, index: number, f: ScanResult, matched: Set<string>): TemplateResult {
    const locale = getLocale();
    const on = index === this.search.chosenEpisode;
    const t0 = Number(ep.startNs / 1_000_000n);
    const t1 = Number(ep.endNs / 1_000_000n);
    const pick = () => void this.pickEpisode(index);
    return html`<tr class=${on ? 'on' : ''} @click=${pick}>
      <td class="ms-col-pick">
        <input type="radio" name=${this.radioGroup} .checked=${on} aria-label=${formatSpan(t0, t1, locale)} @change=${pick} />
      </td>
      <td class="ms-col-when">${formatWhen(t0, t1, locale)}</td>
      <td class="ms-col-len">${formatLength(t1 - t0, locale)}</td>
      <td>
        <span class="ms-matched">${this.matchedOn(ep, f, matched).map((m) => html`<span class="ms-match">${m}</span>`)}</span>
      </td>
      <td class="ms-col-states" title=${ep.entered.join(', ')}>
        ${withSlot(t('model.replayStatesVisited', { n: SLOT }), html`<strong>${formatNumber(ep.entered.length)}</strong>`)}
      </td>
      <td class="ms-col-writes">
        ${ep.writes ? html`<strong>${formatNumber(ep.writes)}</strong>` : html`<span class="ms-none">—</span>`}
        ${ep.errors ? html`<span class="ms-errs">${t('model.replayErrorsShort', { count: ep.errors })}</span>` : nothing}
      </td>
    </tr>`;
  }

  /** The picked result, what either side of it to include, and the buttons that replay it. */
  private renderPicked(f: ScanResult, rows: { ep: ScanEpisode; i: number }[], matched: Set<string>) {
    const index = this.search.chosenEpisode;
    const ep = f.episodes[index];
    if (!ep) return nothing;
    const n = rows.findIndex((r) => r.i === index) + 1;
    const parts = this.matchedOn(ep, f, matched);
    if (ep.writes) parts.push(t('model.replayMachineWrites', { count: ep.writes }));
    const include = INCLUDE_OPTIONS_MS.map((ms) => ({
      value: String(ms),
      label: ms ? t('model.replayIncludeAround', { minutes: ms / 60_000 }) : t('model.replayIncludeExact'),
    }));
    return html`<div class="ms-pick">
        <vitesse-icon class="ms-pick-icon" name="search" size="16"></vitesse-icon>
        <div class="ms-pick-text">
          <span class="ms-pick-label">${t('model.replayPickLabel', { n, total: rows.length })}</span>
          <span class="ms-pick-what"
            ><strong>${formatSpan(Number(ep.startNs / 1_000_000n), Number(ep.endNs / 1_000_000n), getLocale())}</strong>${parts.map(
              (x) => ` · ${x}`,
            )}</span
          >
        </div>
        <span class="ms-pick-controls">
          <span class="ms-label">${t('model.replayInclude')}</span>
          <vitesse-select
            class="ms-include"
            size="md"
            .options=${include}
            .value=${String(this.includeMs)}
            @change=${(e: CustomEvent<{ value: string }>) => {
              this.includeMs = Number(e.detail.value);
              if (this.search.chosenEpisode >= 0) void this.pickEpisode(this.search.chosenEpisode);
            }}
          ></vitesse-select>
          <span class="ms-pick-rule" aria-hidden="true"></span>
          ${this.renderRunButtons(true)}
        </span>
      </div>
      <div class="ms-run-status">${this.renderRunStatus(nothing)}</div>`;
  }

  // ── the chips ────────────────────────────────────────────────────────────

  /*
   * WHAT TO LOOK FOR, SAID BY CLICKING. One chip per state the document
   * declares, then writes and errors. Nothing is typed and nothing is parsed,
   * so nothing can be misread: every label is a state id as the author wrote
   * it, or a phrase from the locale files. A state the coverage check says a
   * replay can never reach is still shown — greyed, with the check's own
   * reason as its tooltip — because a chip missing from the row reads as a
   * state that does not exist. Past a dozen states the row becomes a picker:
   * the chosen ones stay as chips and the rest wait in a select.
   */
  private renderChips() {
    const states = (this.scxml ? this.currentCoverage()?.states : undefined) ?? [];
    const sel = this.search.selection;
    const chosen = new Set(sel.states);
    const toggle = (id: string) => this.search.setSelection(toggleSelectedState(sel, id));
    const chip = (label: string, on: boolean, onClick: () => void) =>
      html`<button type="button" class="ms-chip${on ? ' on' : ''}" aria-pressed=${on ? 'true' : 'false'} @click=${onClick}>
        ${label}
      </button>`;
    const stateChip = (st: StateCoverage) =>
      st.reachable
        ? chip(st.id, chosen.has(st.id), () => toggle(st.id))
        : html`<span class="ms-chip ms-chip-off" aria-disabled="true" title=${`${st.id} ${this.unreachableReason(st)}`}>${st.id}</span>`;
    // Too many states for a row of chips: the chosen ones as chips, the rest behind a picker that
    // never holds a value — picking adds a chip and the picker goes back to "Add a state…".
    const stateControls =
      pickerMode(states.length) === 'chips'
        ? states.map(stateChip)
        : html`${sel.states.map((id) => chip(id, true, () => toggle(id)))}
            <vitesse-select
              class="ms-chip-select"
              size="sm"
              stateless
              placeholder=${t('model.replayFindAddState')}
              .options=${states
                .filter((st) => !chosen.has(st.id))
                .map((st) => ({
                  value: st.id,
                  label: st.reachable ? st.id : t('model.replayFindOptionUnreachable', { state: st.id }),
                  disabled: !st.reachable,
                }))}
              @change=${(e: CustomEvent<{ value: string }>) => toggle(e.detail.value)}
            ></vitesse-select>`;
    return html`<div class="ms-chips">
      ${
        states.length
          ? html`<span class="ms-label">${t('model.replayFindStates')}</span> ${stateControls} <span class="ms-chip-divider"></span>`
          : nothing
      }
      <span class="ms-label">${t('model.replayFindMachine')}</span>
      ${chip(t('model.replayFindChipWrites'), sel.writes, () => this.search.setSelection({ ...sel, writes: !sel.writes }))}
      ${chip(t('model.replayFindChipErrors'), sel.errors, () => this.search.setSelection({ ...sel, errors: !sel.errors }))}
      ${
        isEmptyFilter(sel)
          ? nothing
          : html`<button type="button" class="ms-chip-clear" @click=${() => this.search.setSelection(EMPTY_SELECTION)}>
              ${t('model.replayFindClear')}
            </button>`
      }
    </div>`;
  }

  /*
   * WHAT THE BUTTON WILL DO, said before it is pressed and updated on every
   * click. Two chosen things mean EITHER, and the sentence says "or".
   *
   * WHAT IS BOLD IS WHAT YOU CHOSE. The sentence is a confirmation, so the eye
   * should land on the part your click put there and skip the scaffolding
   * around it: a state name, a write target. Where a chip contributes no noun
   * of its own — a script error, or a write whose targets cannot be named —
   * the clause itself is the contribution and is bold whole. So there is
   * always something bold once a chip is on, and never anything bold before.
   *
   * The bold is inside the translated sentence rather than glued around it, so
   * the words either side stay the locale's own. See `withSlot` and `listOf`.
   */
  private describeSelection(): TemplateResult {
    const s = this.search.selection;
    if (isEmptyFilter(s)) return html`${t('model.replayFindDescAll')}`;
    const strong = (x: string) => html`<strong>${x}</strong>`;
    const parts: TemplateResult[] = [];
    if (s.states.length) {
      const names = listOf(s.states.map(strong), 'disjunction');
      parts.push(withSlot(t('model.replayFindDescStates', { states: SLOT }), names));
    }
    if (s.writes) parts.push(this.describeWrites(strong));
    if (s.errors) parts.push(strong(t('model.replayFindDescErrors')));
    return withSlot(t('model.replayFindDesc', { what: SLOT }), listOf(parts, 'disjunction'));
  }

  /*
   * WHAT "change the machine" MEANS FOR THIS PARTICULAR MODEL.
   *
   * "would have changed the machine" is true of every model that writes
   * anything, and tells a reader nothing about what would have been touched.
   * The wiring knows: a model writes only the PLC symbols its outputs drive, so
   * the sentence can name them.
   *
   * Capped at three: past that the line stops being a hint and becomes an
   * inventory, and the general phrase is the better summary.
   */
  private describeWrites(strong: (x: string) => TemplateResult): TemplateResult {
    const paths = this.model ? plcWritePaths(this.model) : [];
    if (paths.length === 0 || paths.length > 3) return strong(t('model.replayFindDescWrites'));
    const names = listOf([...new Set(paths.map(writeTargetName))].map(strong), 'disjunction');
    return withSlot(t('model.replayFindDescWritesTo', { paths: SLOT }), names);
  }

  /*
   * WHAT A REPLAY CAN FEED THE ROOT'S DOCUMENT: the outside paths its own wiring connects, which
   * the replay drives, and its children's ports when it has children. The wiring is the whole
   * story, because a document reads no path by itself.
   */
  private currentCoverage(): ReplayCoverage | null {
    const m = this.model;
    if (!m) return null;
    const reads = Object.values(connectedInputs(m));
    const ports = m.children.length > 0;
    const key = `${this.scxml}\u0000${JSON.stringify(reads)}\u0000${ports}`;
    if (this.coverage?.key !== key) {
      const tree = parseCoverageTree(this.scxml);
      this.coverage = { key, value: tree ? analyzeReplayCoverage(tree, reads, ports) : null };
    }
    return this.coverage.value;
  }

  /*
   * WHAT A REPLAY CAN TEST, SAID BEFORE THE FIRST RUN.
   *
   * Some states and edges can be known from the document alone to be out of a
   * replay's reach — they wait for an event that recorded history never
   * produces. Finding that out as "never entered" after a run reads as "the data
   * never got there", which sends a person hunting for a better window that
   * does not exist. So it is said up front, with the reason, and with the event
   * that was probably meant when the name is a near miss.
   */
  private renderCoverage() {
    const c = this.scxml ? this.currentCoverage() : null;
    if (!c) return nothing;
    const blockedStates = c.states.filter((st) => !st.reachable);
    if (blockedStates.length === 0 && c.deadEdges.length === 0) {
      return html`<p class="ms-check ms-check-ok">✓ ${t('model.replayCheckAllReachable')}</p>`;
    }
    const q = (s: string) => html`<strong>${s}</strong>`;
    const edgeItem = (e: EdgeCoverage) =>
      e.blocked?.kind === 'event'
        ? html`<li>
            ${q(`${e.source} → ${e.target || e.source}`)} ${t('model.replayCheckEdgeWaits', { event: e.blocked.event })}
            ${this.eventLine(e.blocked)}
          </li>`
        : nothing;
    return html`<div class="ms-check ms-check-warn">
      <p>${t('model.replayCheckHeading')}</p>
      <ul>
        ${blockedStates.map((st) => html`<li>${q(st.id)} ${this.unreachableReason(st)}</li>`)} ${c.deadEdges.map(edgeItem)}
      </ul>
    </div>`;
  }

  private eventLine(b: Extract<Blocked, { kind: 'event' }>): string {
    const reason = {
      outside: t('model.replayCheckWhyOutside'),
      app: t('model.replayCheckWhyApp'),
      ai: t('model.replayCheckWhyAi'),
      'no-controller-inputs': t('model.replayCheckWhyNoController'),
      'no-model-inputs': t('model.replayCheckWhyNoModel'),
    }[b.why];
    return `${reason}${b.suggestion ? ` ${t('model.replayCheckSuggest', { event: b.suggestion })}` : ''}`;
  }

  /** Why a replay can never reach this state, in the check's own words — shared
   *  by the check's list and the greyed chip's tooltip, so the two never disagree. */
  private unreachableReason(st: StateCoverage): string {
    const byEvent = st.blockedBy.find((e) => e.blocked?.kind === 'event');
    if (byEvent && byEvent.blocked?.kind === 'event') {
      return `${t('model.replayCheckStateWaits', { event: byEvent.blocked.event })} ${this.eventLine(byEvent.blocked)}`;
    }
    const sources = [...new Set(st.blockedBy.map((e) => e.source))];
    if (sources.length) return t('model.replayCheckStateFrom', { states: sources.join(', ') });
    return t('model.replayCheckStateNoWayIn');
  }

  /*
   * WHAT THE CHART IS NOT SHOWING, SAID ABOVE THE CHART.
   *
   * A replay that set an input aside draws every lane it could and none it
   * could not — which on screen is a complete-looking picture of an incomplete
   * run. That is worse than the refusal it replaced, because the refusal at
   * least announced itself. So the inputs left out are listed here, in the
   * same bordered block the coverage check uses, each with its reason and with
   * what to do about it: a different window, a different device, a real path.
   */
  private renderSkipped(skipped: SkippedInput[]) {
    if (skipped.length === 0) return nothing;
    const hint: Record<SkippedInput['why'], string> = {
      'window-after': t('model.replaySkippedTryEarlier'),
      'window-before': t('model.replaySkippedTryLater'),
      'no-history': t('model.replaySkippedTryController'),
      'integration-path': t('model.replaySkippedTryBinding'),
      'not-a-path': t('model.replaySkippedTryBinding'),
    };
    return html`<div class="ms-check ms-check-warn">
      <p>${t('model.replaySkippedHeading', { count: skipped.length })}</p>
      <ul>
        ${skipped.map((s) => html`<li><strong>${s.name ?? s.path}</strong> — ${skippedReason(s)} ${hint[s.why]}</li>`)}
      </ul>
    </div>`;
  }

  private renderBody() {
    if (!this.scxml) return html`<p class="ms-note">${t('model.simulateNoSource')}</p>`;
    // Changing a control does not re-run anything. Saying so is the difference
    // between "the button did nothing" and "press Run again".
    const stale = !this.busy && (this.result !== null || this.refusal !== '') && this.ranWith !== this.signature();
    const staleLine = stale ? html`<p class="ms-stale">${t('model.simulateStale')}</p>` : nothing;
    // A refusal is a finished answer, not a failure — it gets the same weight as
    // a result, because "why this could not be replayed" is usually the finding.
    if (this.refusal)
      return html`${staleLine}
        <p class="ms-refusal">${this.refusal}</p>
        ${this.renderCheckFindings()}`;
    if (this.failure)
      return html`${staleLine}
        <p class="ms-failure">${t('model.simulateFailed', { reason: this.failure })}</p>`;
    if (this.result) {
      // A window in which nothing was recorded replays a model that cannot
      // move — which on screen is indistinguishable from a model that had
      // nothing to do. Say which one it was, above the chart.
      const empty =
        this.result.rowCount === 0
          ? html`<p class="ms-refusal">
              ${t('model.simulateNoRows', { symbols: this.result.inputs.map((i) => i.symbolDecoded).join(', ') })}
            </p>`
          : nothing;
      // The viewer opens on the simulation when one arrives, and on the replay
      // otherwise; its own switch moves between the two after that.
      const note = this.noDivergence ? html`<p class="ms-refusal ms-sidecar-note">${this.noDivergence}</p>` : nothing;
      return html`${staleLine}${this.renderSkipped(this.result.skipped)}${empty}${note}
        <vitesse-statechart-replay
          .scxml=${this.scxml}
          .result=${this.result}
          .simulation=${this.simulation}
          .mode=${this.simulation ? 'simulation' : 'replay'}
          .highlightStates=${this.search.selection.states}
        ></vitesse-statechart-replay>
        ${this.renderSidecar()}`;
    }
    if (this.busy) return html`<p class="ms-note">${t('model.simulateRunning')}</p>`;
    // A Simulate refusal can come before any replay: "the AI plane is not connected" is decided
    // first. It used to be drawn only under a replay's chart, so with none it was never seen and
    // the button looked dead.
    if (this.sidecarRefusal || this.sidecarFailure) return this.renderSidecar();
    // Idle, the query card's own hint says what Run does.
    return nothing;
  }

  /*
   * THE SIDECAR PANEL, under the timeline: what the AI wrote, how far to trust
   * it, what it assumed, and the code itself, read-only (decision 6). A refusal
   * takes the panel's place in the server's own words; a sidecar the gates
   * refused twice is shown with its error, so the person can see what it wrote.
   */
  private renderSidecar() {
    if (this.sidecarFailure)
      return html`<p class="ms-failure ms-sidecar-gap">${t('model.simulateSidecarFailed', { reason: this.sidecarFailure })}</p>`;
    if (this.sidecarRefusal) {
      return html`<div class="ms-sidecar-gap">
        ${this.sidecarShown !== null ? html`<p class="ms-failure">${t('model.simulateSidecarGateFailed')}</p>` : nothing}
        <p class="ms-refusal ms-sidecar-refusal">${this.sidecarRefusal}</p>
        ${this.sidecarRemedy ? html`<p class="ms-sidecar-remedy">${t('model.simulateSidecarRemedy', { remedy: this.sidecarRemedy })}</p>` : nothing}
        ${
          this.sidecarShown !== null
            ? html`<vitesse-code-view
                class="ms-sidecar-code"
                .value=${this.sidecarShown}
                language="typescript"
                maxHeight="320px"
              ></vitesse-code-view>`
            : nothing
        }
      </div>`;
    }
    const s = this.sidecar;
    const sim = this.simulation;
    if (!s) return nothing;
    const stale = !this.busy && this.simulatedWith !== this.signature();
    const inputs = sim ? [...sim.inputs, ...sim.neighbours] : [];
    const nameOf = (path: string) => inputs.find((i) => i.path === path)?.symbolDecoded ?? path;
    const notRead = sim ? sim.simulated.filter((x) => !x.readByModel).map((x) => nameOf(x.path)) : [];
    const fits = sim ? sim.shadow.map((f) => this.shadowFit(sim, f.path, nameOf(f.path))) : [];
    /*
     * THE SIDECAR CARD, tinted the green of the line it draws, so the card and the
     * simulated stretch above it read as one thing. The summary first; then each
     * number the AI had to assume, as a box with the value large and its reason
     * small, because those are what a person must judge before trusting the
     * line; then the shadow fit, as a warning only when it is poor. The code is
     * behind "Why these values?" — the answer to that question, for the one
     * person who asks it.
     */
    return html`${stale ? html`<p class="ms-stale ms-sidecar-gap">${t('model.simulateSidecarStale')}</p>` : nothing}
      <section class="ms-sidecar">
        <header class="ms-sc-head">
          <span class="ms-sc-swatch" aria-hidden="true"></span>
          <span class="ms-sidecar-heading">${t('model.simulateSidecarHeading')}</span>
          <span class="ms-sc-draws">${t('model.simulateSidecarDraws')}</span>
          <span class="ms-sc-actions"
            >${button({
              label: t('model.simulateSidecarRegenerate'),
              color: 'neutral',
              size: 'sm',
              disabled: this.busy,
              onClick: () => void this.simulateAi(true),
            })}</span
          >
        </header>
        <p class="ms-sc-intro">${t('model.simulateSidecarIntro')}${s.assumed.length ? ` ${t('model.simulateSidecarIntroGuessed')}` : ''}</p>
        <p class="ms-sidecar-summary">${s.summary}</p>
        ${
          s.assumed.length
            ? html`<ul class="ms-sidecar-assumed">
                ${s.assumed.map((a) => this.renderAssumed(a))}
              </ul>`
            : html`<p class="ms-sidecar-label">${t('model.simulateSidecarAllMeasured')}</p>`
        }
        ${fits.map(
          (f) =>
            html`<p class="ms-sidecar-shadow${f.warn ? ' ms-sc-warn' : ''}">
              ${f.warn ? html`<vitesse-icon name="triangle-alert" size="14"></vitesse-icon>` : nothing}<span>${f.text}</span>
            </p>`,
        )}
        ${notRead.length ? html`<p class="ms-sidecar-label">${t('model.simulateSidecarNotRead', { symbols: notRead.join(', ') })}</p>` : nothing}
        <div class="ms-sc-foot">
          <span class="ms-sidecar-by"
            >${t('model.simulateSidecarWrittenBy', { model: s.model })}${s.reused ? html` · ${t('model.simulateSidecarReusedShort')}` : nothing}</span
          >
          <details class="ms-sc-why">
            <summary>${t('model.simulateSidecarWhy')}</summary>
            <vitesse-code-view class="ms-sidecar-code" .value=${s.code} language="typescript" maxHeight="320px"></vitesse-code-view>
          </details>
        </div>
      </section>`;
  }

  /*
   * ONE ASSUMED NUMBER, AS A BOX. The AI is told to write each as a sentence, and
   * it usually leads with the constant: "TAU_RISE=30s (time constant for CPU
   * rise) because no rise rate was measured". When it does, the box shows what
   * the number is for, the number large, the constant's name in the "assumed"
   * tag, and the reason small. When it does not, the sentence is shown whole —
   * a box that guessed at a value would be worse than one that did not try.
   */
  private renderAssumed(sentence: string): TemplateResult {
    const a = parseAssumed(sentence);
    // The everyday words lead; the constant's name (TAU_RISE) is the code's, so it only rides in
    // the hover text — the AI's whole sentence, which starts with it — for whoever reads the code.
    const tag = html`<span class="ms-sc-tag">${t('model.simulateSidecarAssumedTag')}</span>`;
    return html`<li data-text=${sentence} title=${sentence}>
      ${
        a.value
          ? html`<span class="ms-sc-what">${a.what || a.name}</span>
              <span class="ms-sc-value"><strong>${a.value}</strong>${tag}</span>
              ${a.why ? html`<span class="ms-sc-why-text">${a.why}</span>` : nothing}`
          : html`<span class="ms-sc-value">${tag}</span><span class="ms-sc-why-text">${a.why}</span>`
      }
    </li>`;
  }

  /** How the shadow fitted, in one sentence per simulated symbol (decision 4), and whether it warrants care. */
  private shadowFit(sim: SimulationResult, path: string, symbol: string): { text: string; warn: boolean } {
    const f = sim.shadow.find((x) => x.path === path);
    const plain = (text: string) => ({ text, warn: false });
    if (!f || f.compared < 2) return plain(t('model.simulateSidecarShadowNone', { symbol }));
    if (f.maxError !== null) {
      const recorded = numberExtent(sim.recordedSamples.filter((x) => x.path === path).map((x) => x.value));
      const span = recorded ? recorded.high - recorded.low : 0;
      const care = f.maxError > Math.max(1, span * SHADOW_CARE_SHARE);
      const error = formatNumber(f.maxError, { maximumFractionDigits: 2 });
      return { text: t(care ? 'model.simulateSidecarShadowPoor' : 'model.simulateSidecarShadowGood', { symbol, error }), warn: care };
    }
    if (f.maxShiftSec !== null)
      return plain(t('model.simulateSidecarShadowShift', { symbol, seconds: formatNumber(f.maxShiftSec, { maximumFractionDigits: 1 }) }));
    return plain(t('model.simulateSidecarShadowNone', { symbol }));
  }

  private styles() {
    return html`<style>
      /* The section's own title, with the one-line promise beside it. */
      vitesse-model-simulate .ms-heading {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 4px 10px;
        margin: 0 0 8px;
      }
      vitesse-model-simulate .ms-heading strong {
        font-size: 15px;
        font-weight: 600;
        color: var(--text-primary);
      }
      vitesse-model-simulate .ms-heading .ms-hint {
        margin: 0;
      }
      vitesse-model-simulate .ms-hint,
      vitesse-model-simulate .ms-note {
        color: var(--text-secondary);
        font-size: 12px;
        margin: 0 0 8px;
      }
      vitesse-model-simulate .ms-check {
        font-size: 12px;
        line-height: 1.5;
        margin: 0 0 10px;
      }
      vitesse-model-simulate .ms-check-ok {
        color: var(--text-secondary);
      }
      vitesse-model-simulate .ms-check-warn {
        color: var(--text-primary);
        padding: 8px 10px;
        border: 1px solid var(--border-color);
        border-left: 3px solid var(--yellow);
        border-radius: var(--radius-sm, 6px);
        background: var(--bg-secondary);
      }
      vitesse-model-simulate .ms-check-warn p {
        margin: 0;
      }
      vitesse-model-simulate .ms-check-warn ul {
        margin: 4px 0 0;
        padding-left: 18px;
      }
      /* THE QUERY CARD: the same panel surface as "Read history from" above it. */
      vitesse-model-simulate .ms-query {
        margin: 14px 0 18px;
        padding: 14px 16px 16px;
        background: var(--card-bg);
        border: 1px solid var(--border-color);
        border-radius: 12px;
      }
      vitesse-model-simulate .ms-chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin: 0 0 14px;
        max-height: 104px;
        overflow-y: auto;
      }
      vitesse-model-simulate .ms-chips .ms-label {
        margin-right: 2px;
      }
      /* Off: an outlined pill. On: filled blue. The size of a small button, so a
         row of them reads as controls, not tags. */
      vitesse-model-simulate .ms-chip {
        font: inherit;
        font-size: 13px;
        line-height: 1.2;
        padding: 6px 14px;
        border: 1px solid var(--border-color);
        border-radius: 999px;
        background: transparent;
        color: var(--text-primary);
        cursor: pointer;
        white-space: nowrap;
        transition:
          border-color 0.15s ease,
          background 0.15s ease;
      }
      vitesse-model-simulate .ms-chip:hover {
        border-color: var(--blue);
      }
      vitesse-model-simulate .ms-chip.on {
        border-color: var(--blue);
        background: var(--blue);
        color: var(--on-accent);
      }
      vitesse-model-simulate .ms-chip-off {
        color: var(--text-secondary);
        border-style: dashed;
        cursor: help;
        opacity: 0.7;
      }
      vitesse-model-simulate .ms-chip-divider {
        width: 1px;
        height: 20px;
        margin: 0 4px;
        background: var(--border-color);
      }
      vitesse-model-simulate .ms-chip-select {
        min-width: 150px;
      }
      vitesse-model-simulate .ms-chip-clear {
        font: inherit;
        font-size: 12px;
        padding: 0 4px;
        border: none;
        background: none;
        color: var(--blue);
        cursor: pointer;
      }
      vitesse-model-simulate .ms-chip-clear:hover {
        text-decoration: underline;
      }
      /* "YOUR SEARCH": a blue-tinted inset, so the sentence reads as the thing
         being asked and the button beside it as what asks it. */
      vitesse-model-simulate .ms-search {
        padding: 12px 16px 14px;
        border: 1px solid color-mix(in srgb, var(--blue) 30%, var(--border-color));
        border-radius: 10px;
        background: color-mix(in srgb, var(--blue) 7%, transparent);
      }
      vitesse-model-simulate .ms-search-label {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 0 0 6px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--blue);
      }
      /* Top-aligned: the controls stay level with the sentence's first line
         however many lines it grows to, instead of riding down with its end. */
      vitesse-model-simulate .ms-search-body {
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;
        gap: 10px 24px;
      }
      vitesse-model-simulate .ms-find-desc {
        flex: 1 1 360px;
        margin: 0;
        font-size: 14px;
        line-height: 1.9;
        color: var(--text-secondary);
      }
      /* What you chose, as a tag lifted out of the muted sentence around it. */
      vitesse-model-simulate .ms-find-desc strong {
        padding: 2px 7px;
        border-radius: 5px;
        background: color-mix(in srgb, var(--blue) 22%, transparent);
        color: var(--text-primary);
        font-weight: 600;
        white-space: nowrap;
        box-decoration-break: clone;
        -webkit-box-decoration-break: clone;
      }
      vitesse-model-simulate .ms-find-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-left: auto;
      }
      vitesse-model-simulate .ms-window {
        min-width: 150px;
      }
      vitesse-model-simulate .ms-find-progress {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 10px;
      }
      /* The time range: the preset picker and the stretch's length. */
      vitesse-model-simulate .ms-range-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
      }
      /* The results under the card. */
      vitesse-model-simulate .ms-find {
        margin: 0 0 20px;
      }
      vitesse-model-simulate .ms-find-heading {
        font-size: 12px;
        color: var(--text-primary);
        margin: 10px 0 6px;
      }
      /* WHAT TO REPLAY: the same panel surface as the query card above it. */
      vitesse-model-simulate .ms-results {
        margin: 0 0 18px;
        padding: 14px 16px 12px;
        background: var(--card-bg);
        border: 1px solid var(--border-color);
        border-radius: 12px;
      }
      vitesse-model-simulate .ms-results-head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px 14px;
        margin: 0 0 14px;
      }
      vitesse-model-simulate .ms-results-title {
        font-size: 15px;
        font-weight: 600;
        color: var(--text-primary);
      }
      vitesse-model-simulate .ms-results-meta {
        margin-left: auto;
        font-size: 12px;
        color: var(--text-secondary);
        font-variant-numeric: tabular-nums;
      }
      /* A two-way switch, the chosen side raised. */
      vitesse-model-simulate .ms-seg {
        display: inline-flex;
        gap: 2px;
        padding: 3px;
        border: 1px solid var(--border-color);
        border-radius: 10px;
        background: var(--bg-primary);
      }
      vitesse-model-simulate .ms-seg-tab {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        border: 0;
        border-radius: 7px;
        background: none;
        font: inherit;
        font-size: 13px;
        color: var(--text-secondary);
        cursor: pointer;
      }
      vitesse-model-simulate .ms-seg-tab:hover {
        color: var(--text-primary);
      }
      vitesse-model-simulate .ms-seg-tab:disabled {
        opacity: 0.5;
        cursor: default;
      }
      vitesse-model-simulate .ms-seg-tab:disabled:hover {
        color: var(--text-secondary);
      }
      vitesse-model-simulate .ms-seg-tab.on {
        background: var(--bg-secondary);
        color: var(--text-primary);
        font-weight: 600;
      }
      vitesse-model-simulate .ms-seg-count {
        padding: 0 7px;
        border-radius: 999px;
        background: var(--bg-tertiary);
        font-size: 11px;
        line-height: 18px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }
      /* The results table. */
      vitesse-model-simulate .ms-table {
        border: 1px solid var(--border-color);
        border-radius: 10px;
        overflow: hidden;
      }
      vitesse-model-simulate .ms-table-scroll {
        max-height: 440px;
        overflow: auto;
      }
      vitesse-model-simulate .ms-table table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
      }
      vitesse-model-simulate .ms-table th {
        position: sticky;
        top: 0;
        z-index: 1;
        padding: 10px 12px;
        background: var(--card-bg);
        border-bottom: 1px solid var(--border-color);
        text-align: left;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--text-secondary);
        white-space: nowrap;
      }
      vitesse-model-simulate .ms-table td {
        padding: 11px 12px;
        border-bottom: 1px solid var(--border-color);
        color: var(--text-secondary);
        vertical-align: middle;
      }
      vitesse-model-simulate .ms-table tbody tr {
        cursor: pointer;
      }
      vitesse-model-simulate .ms-table tbody tr:hover td {
        background: var(--bg-secondary);
      }
      /* The picked row: blue-tinted, with a bar down its left edge. */
      vitesse-model-simulate .ms-table tbody tr.on td {
        background: rgb(from var(--blue) r g b / 0.12);
      }
      vitesse-model-simulate .ms-table tbody tr.on td:first-child {
        box-shadow: inset 3px 0 0 var(--blue);
      }
      vitesse-model-simulate .ms-col-pick {
        width: 1%;
        padding-left: 16px;
      }
      vitesse-model-simulate .ms-col-pick input {
        accent-color: var(--blue);
        width: 16px;
        height: 16px;
        margin: 0;
        cursor: pointer;
      }
      vitesse-model-simulate .ms-col-when {
        color: var(--text-primary);
        font-family: var(--font-mono, ui-monospace, monospace);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      vitesse-model-simulate .ms-col-len,
      vitesse-model-simulate .ms-col-states,
      vitesse-model-simulate .ms-col-writes {
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
      vitesse-model-simulate .ms-table strong {
        color: var(--text-primary);
        font-weight: 600;
      }
      vitesse-model-simulate .ms-matched {
        display: inline-flex;
        flex-wrap: wrap;
        gap: 6px;
      }
      vitesse-model-simulate .ms-match {
        padding: 2px 9px;
        border: 1px solid var(--border-color);
        border-radius: 6px;
        font-size: 12px;
        color: var(--text-primary);
        white-space: nowrap;
      }
      vitesse-model-simulate .ms-table tr.on .ms-match {
        border-color: rgb(from var(--blue) r g b / 0.7);
      }
      vitesse-model-simulate .ms-none {
        color: var(--text-tertiary, var(--text-secondary));
      }
      vitesse-model-simulate .ms-errs {
        margin-left: 6px;
        font-size: 12px;
        color: var(--red);
      }
      vitesse-model-simulate .ms-results-more {
        display: block;
        width: 100%;
        padding: 10px;
        border: 0;
        background: none;
        font: inherit;
        font-size: 13px;
        color: var(--blue);
        cursor: pointer;
      }
      vitesse-model-simulate .ms-results-more:hover {
        text-decoration: underline;
      }
      /* The picked result and its buttons: a blue-tinted bar, like "Your search". */
      vitesse-model-simulate .ms-pick {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px 14px;
        margin-top: 14px;
        padding: 12px 16px;
        border: 1px solid color-mix(in srgb, var(--blue) 35%, var(--border-color));
        border-radius: 10px;
        background: color-mix(in srgb, var(--blue) 7%, transparent);
      }
      /* The time range's bar: the same shape, in the card's own neutral. */
      vitesse-model-simulate .ms-pick-range {
        border-color: var(--border-color);
        background: none;
      }
      vitesse-model-simulate .ms-pick-range .ms-pick-icon,
      vitesse-model-simulate .ms-pick-range .ms-pick-label {
        color: var(--text-secondary);
      }
      vitesse-model-simulate .ms-pick-icon {
        color: var(--blue);
        flex-shrink: 0;
      }
      vitesse-model-simulate .ms-pick-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        flex: 1 1 260px;
      }
      vitesse-model-simulate .ms-pick-label {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--blue);
      }
      vitesse-model-simulate .ms-pick-what {
        font-size: 14px;
        color: var(--text-secondary);
      }
      vitesse-model-simulate .ms-pick-what strong {
        color: var(--text-primary);
        font-weight: 600;
      }
      vitesse-model-simulate .ms-pick-controls {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-left: auto;
      }
      vitesse-model-simulate .ms-include {
        min-width: 170px;
      }
      vitesse-model-simulate .ms-pick-rule {
        width: 1px;
        height: 26px;
        margin: 0 4px;
        background: var(--border-color);
      }
      vitesse-model-simulate .ms-run-status {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 8px;
      }
      vitesse-model-simulate .ms-run-status:empty {
        display: none;
      }
      vitesse-model-simulate .ms-results .ms-stale,
      vitesse-model-simulate .ms-results .ms-find-timing {
        margin: 10px 0 0;
      }
      /* Read by a screen reader, not shown: the radio column's header. */
      vitesse-model-simulate .ms-sr {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
      }
      vitesse-model-simulate .ms-controls {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        margin: 0 0 14px;
      }
      vitesse-model-simulate .ms-label {
        color: var(--text-secondary);
        font-size: 12px;
      }
      /* "Read history from": the house panel surface (card-bg, border, 12px
         radius), its header a full-width button so the whole line folds it. */
      vitesse-model-simulate .ms-src {
        margin: 14px 0 18px;
        background: var(--card-bg);
        border: 1px solid var(--border-color);
        border-radius: 12px;
        overflow: hidden;
      }
      vitesse-model-simulate .ms-src-head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px 10px;
        width: 100%;
        box-sizing: border-box;
        padding: 12px 16px;
        border: 0;
        background: none;
        font: inherit;
        color: var(--text-primary);
        text-align: left;
        cursor: pointer;
      }
      vitesse-model-simulate .ms-src-head:hover .ms-src-title {
        color: var(--blue);
      }
      vitesse-model-simulate .ms-src-chev {
        color: var(--text-secondary);
        flex-shrink: 0;
      }
      vitesse-model-simulate .ms-src-title {
        font-size: 13px;
        font-weight: 600;
      }
      vitesse-model-simulate .ms-src-meta {
        font-size: 12px;
        color: var(--text-secondary);
      }
      vitesse-model-simulate .ms-src-chips {
        min-width: 0;
      }
      vitesse-model-simulate .ms-src-chips .base-chip {
        padding: 2px 8px;
        border-radius: 6px;
        font-family: inherit;
      }
      vitesse-model-simulate .ms-src-status {
        margin-left: auto;
      }
      /* One grid for every row, so the pickers line up whatever the symbols' length. */
      vitesse-model-simulate .ms-src-table {
        display: grid;
        grid-template-columns: minmax(90px, 1fr) minmax(160px, 3fr) minmax(180px, 240px);
      }
      vitesse-model-simulate .ms-src-row {
        display: contents;
      }
      vitesse-model-simulate .ms-src-row > * {
        padding: 8px 16px;
        border-top: 1px solid var(--border-color);
        min-width: 0;
      }
      vitesse-model-simulate .ms-src-cols > * {
        padding-top: 7px;
        padding-bottom: 7px;
        background: var(--bg-secondary);
        font-size: 11px;
        font-weight: 600;
        color: var(--text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      vitesse-model-simulate .ms-src-device {
        font-size: 13px;
        font-weight: 600;
        color: var(--text-primary);
        line-height: 30px; /* the select's height, so the name sits on its line */
      }
      vitesse-model-simulate .ms-src-symbols {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 2px;
        font-size: 13px;
      }
      vitesse-model-simulate .ms-src-symbol {
        display: flex;
        align-items: baseline;
        gap: 8px;
        white-space: nowrap;
        min-width: 0;
        line-height: 26px;
      }
      vitesse-model-simulate .ms-src-sym {
        color: var(--text-secondary);
        overflow: hidden;
        text-overflow: ellipsis;
      }
      vitesse-model-simulate .ms-src-arrow {
        color: var(--text-secondary);
        opacity: 0.6;
      }
      vitesse-model-simulate .ms-src-input {
        font-weight: 600;
        color: var(--text-primary);
      }
      /* "Show N more" / "Show less": a link-styled button under the symbols. */
      vitesse-model-simulate .ms-src-more {
        align-self: flex-start;
        padding: 0;
        border: none;
        background: none;
        font: inherit;
        font-size: 12px;
        color: var(--blue);
        cursor: pointer;
      }
      vitesse-model-simulate .ms-src-more:hover {
        text-decoration: underline;
      }
      vitesse-model-simulate .ms-src-fixed {
        display: block;
        font-size: 13px;
        color: var(--text-secondary);
        line-height: 30px;
      }
      /* Phone width: each device becomes a small block, the picker under its symbols. */
      @media (max-width: 600px) {
        vitesse-model-simulate .ms-src-table {
          grid-template-columns: 1fr;
        }
        vitesse-model-simulate .ms-src-cols {
          display: none;
        }
        vitesse-model-simulate .ms-src-row > * {
          border-top: 0;
          padding: 4px 16px;
        }
        vitesse-model-simulate .ms-src-row > :first-child {
          border-top: 1px solid var(--border-color);
          padding-top: 10px;
        }
        vitesse-model-simulate .ms-src-row > :last-child {
          padding-bottom: 10px;
        }
      }
      vitesse-model-simulate .ms-find-timing {
        margin: 0 0 6px;
        color: var(--text-secondary);
        font-size: 12px;
        font-variant-numeric: tabular-nums;
      }
      vitesse-model-simulate .ms-progress {
        color: var(--text-secondary);
        font-size: 12px;
        font-variant-numeric: tabular-nums;
      }
      vitesse-model-simulate .ms-refusal,
      vitesse-model-simulate .ms-failure {
        font-size: 12px;
        line-height: 1.5;
        margin: 0;
        padding: 8px 10px;
        border-radius: var(--radius-sm, 6px);
        border: 1px solid var(--border-color);
        background: var(--bg-secondary);
      }
      vitesse-model-simulate .ms-refusal {
        color: var(--text-primary);
        border-left: 3px solid var(--yellow);
      }
      /* The check's findings, under its refusal: one per line, the line number aligned. */
      vitesse-model-simulate .ms-findings {
        list-style: none;
        margin: 6px 0 0;
        padding: 0;
        display: grid;
        gap: 4px;
        font-size: 12px;
        line-height: 1.5;
      }
      vitesse-model-simulate .ms-findings li {
        display: grid;
        grid-template-columns: auto auto minmax(0, 1fr);
        gap: 8px;
        align-items: baseline;
      }
      vitesse-model-simulate .ms-finding-line {
        color: var(--text-secondary);
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      vitesse-model-simulate .ms-finding-code {
        font-family: var(--font-mono, ui-monospace, monospace);
        color: var(--red);
        white-space: nowrap;
      }
      vitesse-model-simulate .ms-finding-message {
        color: var(--text-primary);
        overflow-wrap: anywhere;
      }
      vitesse-model-simulate .ms-findings .ms-finding-more {
        display: block;
        color: var(--text-secondary);
      }
      vitesse-model-simulate .ms-stale {
        font-size: 12px;
        color: var(--yellow);
        margin: 0 0 8px;
      }
      vitesse-model-simulate .ms-failure {
        color: var(--text-primary);
        border-left: 3px solid var(--red);
      }
      vitesse-model-simulate .ms-sidecar-gap {
        margin-top: 14px;
      }
      /* What to do about a refusal: quieter than the refusal, right under it. */
      vitesse-model-simulate .ms-sidecar-remedy {
        margin: 0;
        font-size: 12px;
        color: var(--text-secondary);
      }
      vitesse-model-simulate .ms-sidecar-note {
        margin-bottom: 10px;
      }
      vitesse-model-simulate .ms-sidecar-gap > * + * {
        margin-top: 8px;
      }
      /* THE SIDECAR CARD: the house panel shape, in the simulated line's green. */
      vitesse-model-simulate .ms-sidecar {
        margin-top: 14px;
        padding: 12px 16px 14px;
        border: 1px solid rgb(from var(--green) r g b / 0.35);
        border-radius: 12px;
        background: rgb(from var(--green) r g b / 0.05);
      }
      vitesse-model-simulate .ms-sc-head {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0 0 8px;
      }
      /* A short stroke of the line the sidecar draws — the card's legend. */
      vitesse-model-simulate .ms-sc-swatch {
        width: 14px;
        height: 2px;
        border-radius: 1px;
        background: var(--green);
        flex-shrink: 0;
      }
      vitesse-model-simulate .ms-sidecar-heading {
        font-size: 13px;
        font-weight: 600;
        color: var(--text-primary);
      }
      vitesse-model-simulate .ms-sc-draws {
        font-size: 12px;
        color: var(--green);
      }
      vitesse-model-simulate .ms-sc-actions {
        margin-left: auto;
      }
      /* What this card is, in our words, before the AI's own. */
      vitesse-model-simulate .ms-sc-intro {
        font-size: 12px;
        line-height: 1.5;
        color: var(--text-secondary);
        margin: 0 0 6px;
      }
      vitesse-model-simulate .ms-sidecar-summary {
        font-size: 13px;
        line-height: 1.5;
        color: var(--text-primary);
        margin: 0 0 12px;
      }
      /* The assumed numbers: one box each, the value large. */
      vitesse-model-simulate .ms-sidecar-assumed {
        list-style: none;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 10px;
        margin: 0 0 12px;
        padding: 0;
      }
      vitesse-model-simulate .ms-sidecar-assumed li {
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
        padding: 10px 12px;
        border: 1px solid var(--border-color);
        border-radius: 8px;
        background: rgb(from var(--card-bg) r g b / 0.6);
      }
      /* The AI writes the title as a clause ("how fast …"); a box heading starts with a capital. */
      vitesse-model-simulate .ms-sc-what::first-letter {
        text-transform: uppercase;
      }
      vitesse-model-simulate .ms-sc-what {
        font-size: 12px;
        color: var(--text-secondary);
      }
      vitesse-model-simulate .ms-sc-value {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
      }
      vitesse-model-simulate .ms-sc-value strong {
        font-size: 17px;
        font-weight: 600;
        color: var(--text-primary);
        font-variant-numeric: tabular-nums;
      }
      vitesse-model-simulate .ms-sc-tag {
        font-size: 11px;
        line-height: 1.4;
        padding: 1px 7px;
        border: 1px solid rgb(from var(--yellow) r g b / 0.5);
        border-radius: 999px;
        color: var(--yellow);
        white-space: nowrap;
      }
      vitesse-model-simulate .ms-sc-why-text {
        font-size: 12px;
        line-height: 1.45;
        color: var(--text-secondary);
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      vitesse-model-simulate .ms-sidecar-shadow,
      vitesse-model-simulate .ms-sidecar-label {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        font-size: 12px;
        line-height: 1.5;
        color: var(--text-secondary);
        margin: 0 0 8px;
      }
      vitesse-model-simulate .ms-sc-warn {
        color: var(--yellow);
      }
      vitesse-model-simulate .ms-sc-warn vitesse-icon {
        flex-shrink: 0;
        margin-top: 2px;
      }
      vitesse-model-simulate .ms-sc-foot {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 4px 12px;
        margin-top: 4px;
      }
      vitesse-model-simulate .ms-sidecar-by {
        font-size: 12px;
        color: var(--text-secondary);
      }
      vitesse-model-simulate .ms-sc-why summary {
        font-size: 12px;
        color: var(--blue);
        cursor: pointer;
        list-style: none;
      }
      vitesse-model-simulate .ms-sc-why summary::-webkit-details-marker {
        display: none;
      }
      vitesse-model-simulate .ms-sc-why summary:hover {
        text-decoration: underline;
      }
      vitesse-model-simulate .ms-sc-why[open] {
        flex-basis: 100%;
      }
      vitesse-model-simulate .ms-sidecar-code {
        display: block;
        margin: 8px 0 0;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm, 6px);
        overflow: hidden;
      }
      /* The editor takes its container's height and nothing here gave it one, so
         the code showed as an empty strip. Monaco's automatic layout redraws it
         when "Why these values?" opens and the box gets its size. */
      vitesse-model-simulate .ms-sidecar-code .code-view {
        height: 320px;
      }
    </style>`;
  }
}

/** A write target as a person reads it: the symbol or variable at the end of the
 *  path, not the whole `controllers/tank-01/symbols/…` spelling. */
function writeTargetName(path: string): string {
  const c = classifyBindingTarget(path);
  return c.kind === 'controller' ? c.symbol : c.kind === 'model' ? c.variable : path;
}

/** The document and the group it runs as, hashed: what a kept sidecar was written for. */
async function fingerprint(scxml: string, group: GroupSpec | undefined): Promise<string> {
  const bytes = new TextEncoder().encode(`${scxml}\u0000${JSON.stringify(group ?? null)}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** The kept sidecar for a model, if it was written for this exact document and wiring. */
function readCache(modelId: string, hash: string): CachedSidecar | null {
  try {
    const raw = localStorage.getItem(SIDECAR_CACHE + modelId);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<CachedSidecar>;
    if (v.hash !== hash || typeof v.code !== 'string' || typeof v.js !== 'string' || !Array.isArray(v.assumed)) return null;
    return {
      hash,
      code: v.code,
      js: v.js,
      summary: v.summary ?? '',
      assumed: v.assumed.filter((a): a is string => typeof a === 'string'),
      model: v.model ?? '',
    };
  } catch {
    return null; // storage refused or absent: nothing kept, nothing lost
  }
}

function writeCache(modelId: string, sidecar: CachedSidecar | null): void {
  try {
    if (sidecar) localStorage.setItem(SIDECAR_CACHE + modelId, JSON.stringify(sidecar));
    else localStorage.removeItem(SIDECAR_CACHE + modelId);
  } catch {
    /* a full or blocked store only costs the next press a call */
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-model-simulate': ModelSimulateSection;
  }
}
